import { Injectable } from '@angular/core';
import { AbstractHttpService } from './abstract-http-service';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BulkUpdateRequestModel } from '../model/bulk-update-request.model';
import { BulkUpdateCampaignPreviewModel } from '../model/bulk-update-campaign-preview.model';
import {PreviewCampaignBulkUpdateModel} from '../model/preview-campaign-bulk-update.model';
import { BulkUpdateExecutionSummaryModel} from '../model/bulk-update-execution-summary.model';
import { BulkUpdateExecutionDetailModel } from '../model/bulk-update-execution-detail.model';

@Injectable({
  providedIn: 'root'
})
export class BulkUpdateService extends AbstractHttpService {

  options = {
    headers: this.acceptJsonHeaders,
    withCredentials: true
  };

  constructor(private httpClient: HttpClient) {
    super();
  }

  /**
   * Send a bulk update request to the server
   * @param bulkUpdateData The bulk update request data
   * @returns Observable with the response from the server
   */
  sendBulkUpdateRequest(bulkUpdateData: BulkUpdateRequestModel): Observable<any> {
    const url = `//${environment.autoDomain}:${environment.autoPort}/api/media-bulk-update`;

    return this.httpClient.post(url, bulkUpdateData, {
      headers: this.contentTypeJsonHeaders
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Method to get the preview of the bulk update request.
   * Supports either strategy or expression (and back-compat campaignExpression) via GET params.
   * @param previewModel
   */
  getBulkUpdatePreview(previewModel: BulkUpdateCampaignPreviewModel): Observable<PreviewCampaignBulkUpdateModel> {
    const url = `//${environment.autoDomain}:${environment.autoPort}/api/media-bulk-update-preview`;

    // Unified param building: prefer strategy if provided; otherwise send expression.
    const params: any = {
      accountId: previewModel.accountId,
      campaignType: previewModel.campaignType,
      campaignStrategy: previewModel.campaignStrategy,
      expression: previewModel.expression
    };

    return this.httpClient.get<PreviewCampaignBulkUpdateModel>(url, {
      headers: this.acceptJsonHeaders,
      withCredentials: true,
      params
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Send a bulk update filter request to the server and gets back a list of MPOP accounts based on the filter criteria.
   *
   * @param filters The filter request containing account ID and filter criteria
   * @returns Observable with the applied filters containing account IDs and names
   */
  getAccountPreviewFromFilters(filters): Observable<{accountNames: [], accountIds: []}> {
    return this.httpClient.post<{accountNames: [], accountIds: []}>(
      `//${environment.autoDomain}:${environment.autoPort}/api/media-bulk-update-filter`,
      filters,
      this.options
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Get all OEM programs from the API
   * @returns Observable with the list of OEM programs
   */
  getOemPrograms(): Observable<{id: number, name: string}[]> {
    return this.httpClient.get<{id: number, name: string}[]>(
      `//${environment.autoDomain}:${environment.autoPort}/api/oem-programs`,
      this.options
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Parse CSV file and convert to an associative array with ad_group as the key
   * @param file The CSV file to parse
   * @returns Promise with the parsed CSV data
   */
  getBulkUpdateExecutions(): Observable<BulkUpdateExecutionSummaryModel[]> {
    const url = `//${environment.autoDomain}:${environment.autoPort}/api/media-bulk-update-executions`;
    return this.httpClient.get<BulkUpdateExecutionSummaryModel[]>(url, this.options).pipe(
      catchError(this.handleError)
    );
  }

  parseCSVFile(file: File): Promise<Record<string, any>> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        let result = null;
        let errorMessage = null;

        try {
          const contents = e.target.result;
          const lines = contents.split('\n');

          if (lines.length < 2) {
            errorMessage = 'CSV file must have at least a header row and one data row';
          } else {
            const headers = lines[0].split(',').map((header) => header.trim());

            const adGroupIndex = headers.findIndex((header) =>
              header.toLowerCase() === 'ad group');

            if (adGroupIndex === -1) {
              errorMessage = 'CSV file must contain an "Ad group" column';
            } else {
              // Parse data rows into an associative array with ad_group as the key
              const data: Record<string, any> = {};
              for (let i = 1; i < lines.length; i++) {
                const line = lines[i].trim();
                if (!line) continue; // Skip empty lines

                const values = line.split(',');
                const row = {};

                // Get the ad group name
                let adGroupName = '';
                if (values[adGroupIndex]) {
                  adGroupName = values[adGroupIndex].trim();
                }

                if (!adGroupName) {continue;}

                // Create object with header keys and row values
                headers.forEach((header, index) => {
                  let headerValue = '';
                  if (index >= 0 && index < values.length && values[index]) {
                    headerValue = values[index].trim();
                  }
                  row[header] = headerValue;
                });

                data[adGroupName] = row;
              }

              result = data;
            }
          }

          if (errorMessage) {
            reject(errorMessage);
          } else {
            resolve(result);
          }
        } catch (error) {
          reject(`Error parsing CSV file: ${error}`);
        }
      };

      reader.onerror = () => {
        reject('Error reading CSV file');
      };

      reader.readAsText(file);
    });
  }

  /**
   * Get execution details (rows) for a specific execution (batch)
   */
  getBulkUpdateExecutionDetails(executionId: number | string): Observable<BulkUpdateExecutionDetailModel[]> {
    const url = `//${environment.autoDomain}:${environment.autoPort}/api/media-bulk-update-executions/${executionId}`;
    return this.httpClient.get<BulkUpdateExecutionDetailModel[]>(url, this.options).pipe(
      catchError(this.handleError)
    );
  }
}
